import { apiSlice } from "../../app/apiSlice";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import type {
	CabanaBookingDetail,
	CabanaListItem,
} from "../../pages/CabanaBooking/cabanaBooking.types";
import { mapCabanaDetail, mapCabanaListItem } from "./cabana.mappers";
import type { CabanaSortBy, CabanaUpsertPayload } from "./cabana.types";

export interface CabanasListMeta {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
	hasPrevious: boolean;
	hasNext: boolean;
}

export interface CabanasListResponse {
	lastSyncedAt?: string;
	cabanas: CabanaListItem[];
	pagination: CabanasListMeta;
}

export interface GetCabanasArgs {
	page?: number;
	limit?: number;
	sortBy?: CabanaSortBy | string;
	sortOrder?: "asc" | "desc";
	search?: string;
	source?: string;
	status?: string;
}

export interface UpdateCabanaRequest {
	id: string;
	body: CabanaUpsertPayload;
}

function parseListResponse(raw: unknown): CabanasListResponse {
	const root = (raw ?? {}) as Record<string, unknown>;
	const data =
		root.data && typeof root.data === "object"
			? (root.data as Record<string, unknown>)
			: {};
	const cabanasRaw = Array.isArray(data.cabanas) ? data.cabanas : [];
	const paginationRaw =
		data.pagination && typeof data.pagination === "object"
			? (data.pagination as Record<string, unknown>)
			: {};

	const lastSyncedAt =
		typeof data.lastSyncedAt === "string" ? data.lastSyncedAt : undefined;

	return {
		lastSyncedAt,
		cabanas: cabanasRaw.map((entry) => mapCabanaListItem(entry)),
		pagination: {
			page: Number(paginationRaw.page ?? 1),
			limit: Number(paginationRaw.limit ?? 10),
			total: Number(paginationRaw.total ?? cabanasRaw.length),
			totalPages: Number(paginationRaw.totalPages ?? 1),
			hasPrevious: Boolean(paginationRaw.hasPrevious ?? false),
			hasNext: Boolean(paginationRaw.hasNext ?? false),
		},
	};
}

function parseDetailResponse(raw: unknown): CabanaBookingDetail {
	const root = (raw ?? {}) as Record<string, unknown>;
	const data =
		root.data && typeof root.data === "object" && !Array.isArray(root.data)
			? root.data
			: root;
	return mapCabanaDetail(data);
}

export const cabanasApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getCabanas: builder.query<CabanasListResponse, GetCabanasArgs | undefined>({
			query: (args) => {
				const params: Record<string, string | number> = {
					page: args?.page ?? 1,
					limit: args?.limit ?? 10,
					sortBy: args?.sortBy ?? "DEFAULT",
					sortOrder: args?.sortOrder ?? "desc",
				};
				if (args?.search?.trim()) params.search = args.search.trim();
				if (args?.source && args.source !== "ALL") params.source = args.source;
				if (args?.status && args.status !== "ALL") {
					params.status = args.status;
				}
				return {
					url: API_ENDPOINTS.cabanas.list,
					params,
				};
			},
			transformResponse: parseListResponse,
			providesTags: (result) =>
				result
					? [
							{ type: "Cabana", id: "LIST" },
							...result.cabanas.map((c) => ({
								type: "Cabana" as const,
								id: c.id,
							})),
						]
					: [{ type: "Cabana", id: "LIST" }],
		}),
		getCabanaById: builder.query<CabanaBookingDetail, string>({
			query: (id) => API_ENDPOINTS.cabanas.detail(id),
			transformResponse: parseDetailResponse,
			providesTags: (_result, _err, id) => [{ type: "Cabana", id }],
		}),
		createCabana: builder.mutation<CabanaBookingDetail, CabanaUpsertPayload>({
			query: (body) => ({
				url: API_ENDPOINTS.cabanas.create,
				method: "POST",
				body,
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: [{ type: "Cabana", id: "LIST" }],
		}),
		updateCabana: builder.mutation<CabanaBookingDetail, UpdateCabanaRequest>({
			query: ({ id, body }) => ({
				url: API_ENDPOINTS.cabanas.update(id),
				method: "PATCH",
				body,
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: (_result, _err, { id }) => [
				{ type: "Cabana", id },
				{ type: "Cabana", id: "LIST" },
			],
		}),
		publishCabana: builder.mutation<CabanaBookingDetail, string>({
			query: (id) => ({
				url: API_ENDPOINTS.cabanas.publish(id),
				method: "POST",
				body: {},
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: (_result, _err, id) => [
				{ type: "Cabana", id },
				{ type: "Cabana", id: "LIST" },
			],
		}),
		deactivateCabana: builder.mutation<CabanaBookingDetail, string>({
			query: (id) => ({
				url: API_ENDPOINTS.cabanas.deactivate(id),
				method: "POST",
				body: {},
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: (_result, _err, id) => [
				{ type: "Cabana", id },
				{ type: "Cabana", id: "LIST" },
			],
		}),
		activateCabana: builder.mutation<CabanaBookingDetail, string>({
			query: (id) => ({
				url: API_ENDPOINTS.cabanas.activate(id),
				method: "POST",
				body: {},
			}),
			transformResponse: parseDetailResponse,
			invalidatesTags: (_result, _err, id) => [
				{ type: "Cabana", id },
				{ type: "Cabana", id: "LIST" },
			],
		}),
		deleteCabana: builder.mutation<void, string>({
			query: (id) => ({
				url: API_ENDPOINTS.cabanas.delete(id),
				method: "DELETE",
			}),
			invalidatesTags: (_result, _err, id) => [
				{ type: "Cabana", id },
				{ type: "Cabana", id: "LIST" },
			],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetCabanasQuery,
	useGetCabanaByIdQuery,
	useCreateCabanaMutation,
	useUpdateCabanaMutation,
	usePublishCabanaMutation,
	useDeactivateCabanaMutation,
	useActivateCabanaMutation,
	useDeleteCabanaMutation,
} = cabanasApi;
